/**
 * solarCalc.js — pure Step-4 calculator engine. Exact mirror of backend/quick_calc.py
 * (pinned by backend/tests/test_iter48_calculator.py). Keep both in sync.
 */
const BOS_SHARE = 0.40, PANEL_BENCH_SHARE = 0.45, INVERTER_BENCH_SHARE = 0.15;
// Iter 52: BOS lump split into three priced lines, each with its own GST% and margin%
export const BOS_SPLIT = { structure: 0.40, cabling: 0.25, installation: 0.35 };
const ROOF_SQFT_PER_KW = 100, BATTERY_HEADROOM = 1.2;

export const num = (v, d = 0) => { const n = parseFloat(v); return Number.isFinite(n) ? n : d; };
const isSet = (v) => v !== undefined && v !== null && v !== '';
const round = (v, dp = 0) => { const f = 10 ** dp; return Math.round(v * f) / f; };

// Iter 52: no blanket default — null means "missing" and is flagged, never silently filled
export const pct = (v) => (v === undefined || v === null || v === '' ? null : num(v, 0));

export const sellPrice = (item) => {
  if (!item) return 0;
  return num(item.unit_price) * (1 + (pct(item.margin_pct) || 0) / 100);
};

/** Single cash-rounding rule: step 1/10/100 × nearest/up/down. Totals only. */
export const roundCash = (value, config) => {
  const step = num(config?.rounding_step, 1) || 1;
  const mode = config?.rounding_mode || 'nearest';
  if (mode === 'up') return Math.ceil(value / step) * step;
  if (mode === 'down') return Math.floor(value / step) * step;
  return Math.round(value / step) * step;
};

export const pmSuryaGharReference = (kw, config) => {
  const cfg = (config || {}).pm_surya_ghar || {};
  const slabs = [...(cfg.slabs || [])].sort((a, b) => num(a.upto_kw) - num(b.upto_kw));
  let amount = 0;
  slabs.forEach(s => { if (kw >= num(s.upto_kw)) amount = num(s.amount); });
  return Math.min(amount, num(cfg.cap, 78000));
};

/** Iter 59 — mirror of backend pricing_slabs.pick_slab / price_for. Boundary → lower slab; latest effective_from ≤ date wins. */
export function pickSlab(slabs, value, onDate) {
  const day = (onDate || new Date().toISOString()).slice(0, 10);
  const latest = new Map();
  (slabs || []).forEach(s => {
    const eff = String(s.effective_from || '0000-01-01').slice(0, 10);
    if (eff > day) return;
    const hi = s.to_value === null || s.to_value === undefined || s.to_value === '' ? null : num(s.to_value);
    const key = `${num(s.from_value)}|${hi === null ? '' : hi}`;
    const cur = latest.get(key);
    if (!cur || eff > String(cur.effective_from || '').slice(0, 10)) latest.set(key, s);
  });
  const matches = [...latest.values()].filter(s => { const lo = num(s.from_value); const hi = s.to_value === null || s.to_value === undefined || s.to_value === '' ? null : num(s.to_value); return value >= lo && (hi === null || value <= hi); });
  if (!matches.length) return null;
  return matches.reduce((a, b) => (num(a.from_value) <= num(b.from_value) ? a : b));
}
export function slabPriceFor(doc, value, onDate) {
  if (!doc || doc.active === false || !(value > 0)) return null;
  const s = pickSlab(doc.slabs, value, onDate);
  if (!s) return null;
  const rate = num(s.rate_per_unit); const total = Math.round(rate * value);
  const gst = doc.gst_pct === null || doc.gst_pct === undefined || doc.gst_pct === '' ? null : num(doc.gst_pct);
  return { category: doc.category, unit: doc.unit, rate_per_unit: rate, value, total, from_value: s.from_value, to_value: s.to_value ?? null, effective_from: s.effective_from, gst_pct: gst, gst_amount: gst === null ? null : Math.round(total * gst / 100), gst_missing: gst === null };
}

/** Panel / inverter for a quote: picked from Inventory, typed in (not in Inventory), or not part of this job.
 *  A typed-in item is priced at what you enter (that is the selling price, so margin 0). */
export const ITEM_MODES = ['inventory', 'custom', 'none'];
export function customItem(c, kind) {
  if (!c || !(num(c.unit_price) > 0)) return null;
  return { id: `custom-${kind}`, custom: true, name: (c.name || '').trim() || (kind === 'panel' ? 'Solar panel' : 'Inverter'), unit_price: num(c.unit_price),
    gst_percentage: pct(c.gst_percentage), margin_pct: 0, specs: kind === 'panel' ? { wattage: num(c.wattage) } : { rated_kw: num(c.rated_kw) } };
}

export function computeQuick(inputs, config, panel, inverter, battery, slabDoc) {
  const systemType = inputs.system_type || 'on-grid';
  const panelOff = inputs.panel_mode === 'none', inverterOff = inputs.inverter_mode === 'none';
  if (panelOff) panel = null;
  if (inverterOff) inverter = null;
  const overrides = inputs.overrides || {};
  const customerType = inputs.customer_type || 'residential';
  const warnings = [];
  const warn = (field, message) => warnings.push({ field, message });
  const fmt = (v) => Math.round(v).toLocaleString('en-IN');

  const costMap = (config || {}).cost_per_kwp || {};
  const baseKwp = num(costMap['on-grid'], 55000);
  const typeKwp = num(costMap[systemType], baseKwp);
  const sy = num(config?.default_specific_yield, 4.4) || 4.4;
  const tariff = num(inputs.tariff_per_unit) || num(config?.default_tariff_per_unit, 8) || 8;

  // Bill / units are entered per bill; TN domestic bills come every 2 months.
  const billingCycle = inputs.billing_cycle === 'bimonthly' ? 'bimonthly' : 'monthly';
  const period = billingCycle === 'bimonthly' ? 2 : 1;
  const gridTied = systemType === 'on-grid' || systemType === 'hybrid';
  const exportRate = gridTied ? Math.max(num(inputs.export_rate), 0) : 0;
  const networkCharge = gridTied ? Math.max(num(inputs.network_charge), 0) : 0;
  const networkBasis = inputs.network_charge_basis === 'per_kw_month' ? 'per_kw_month' : 'per_unit';
  let units = num(inputs.monthly_eb_units) / period;
  const bill = num(inputs.monthly_eb_bill) / period;
  let unitsSource = 'entered';
  if (units <= 0 && bill > 0) { units = Math.round(bill / tariff); unitsSource = 'from_bill'; }
  if (units <= 0) unitsSource = 'none';
  const daily = units > 0 ? units / 30 : 0;

  let kwAuto = daily > 0 ? Math.ceil((daily / sy) * 2) / 2 : 0;
  const roofSqft = num(inputs.roof_area_sqft);
  const roofCapKw = roofSqft > 0 ? Math.floor((roofSqft / ROOF_SQFT_PER_KW) * 2) / 2 : null;
  let roofLimited = false;
  if (roofCapKw !== null && kwAuto > roofCapKw) { kwAuto = roofCapKw; roofLimited = true; }
  const kw = isSet(overrides.system_size_kw) ? num(overrides.system_size_kw) : kwAuto;
  if (roofCapKw !== null && kw > roofCapKw) warn('system_size_kw', `Roof of ${roofSqft} sq ft fits about ${roofCapKw} kW (${ROOF_SQFT_PER_KW} sq ft per kW). ${kw} kW may not fit.`);
  else if (roofLimited) warn('system_size_kw', `Size limited by roof: ${roofSqft} sq ft fits about ${roofCapKw} kW.`);

  const panelW = panel ? num(panel.specs?.wattage) : 0;
  if (panel && panelW <= 0) warn('panel_item_id', `'${panel.name}' has no wattage in Inventory — set the panel count manually or add specs.wattage.`);
  const panelCountAuto = kw > 0 && panelW > 0 ? Math.ceil(kw * 1000 / panelW) : null;
  const panelCount = panelOff ? 0 : (isSet(overrides.panel_count) ? Math.trunc(num(overrides.panel_count)) : (panelCountAuto || 0));

  const inverterKw = inverter ? num(inverter.specs?.rated_kw) : 0;
  if (inverter && inverterKw > 0 && kw > 0) {
    if (inverterKw < kw * 0.8) warn('inverter_item_id', `Inverter is ${inverterKw} kW for a ${kw} kW array — undersized (below 80%). Pick a larger inverter.`);
    else if (inverterKw > kw * 1.5) warn('inverter_item_id', `Inverter is ${inverterKw} kW for a ${kw} kW array — oversized (over 150%). A smaller unit would cost less.`);
  }

  const needsBattery = systemType === 'hybrid' || systemType === 'off-grid';
  const backupHours = num(inputs.backup_hours) || (systemType === 'off-grid' ? 8 : 4);
  let batteryKwhNeeded = 0, batteryUnitKwh = 0, batteryCountAuto = null, batteryCount = 0;
  if (needsBattery && daily > 0) {
    let dod = battery ? num(battery.specs?.dod_pct) : 0;
    dod = dod > 0 ? dod / 100 : 0.8;
    batteryKwhNeeded = round(daily * backupHours / 24 * BATTERY_HEADROOM / dod, 2);
    batteryUnitKwh = battery ? num(battery.specs?.kwh) : 0;
    if (battery && batteryUnitKwh <= 0) warn('battery_item_id', `'${battery.name}' has no kWh in Inventory — set the battery count manually or add specs.kwh.`);
    if (batteryUnitKwh <= 0) batteryUnitKwh = num(config?.battery_unit_kwh, 5) || 5;
    batteryCountAuto = batteryKwhNeeded > 0 ? Math.ceil(batteryKwhNeeded / batteryUnitKwh) : 0;
  }
  if (needsBattery) batteryCount = isSet(overrides.battery_count) ? Math.trunc(num(overrides.battery_count)) : (batteryCountAuto || 0);

  const pricingIssues = [];
  const itemPricing = (item, field, label) => {
    if (!item) return [null, null];
    const g = pct(item.gst_percentage), m = pct(item.margin_pct);
    if (g === null) { pricingIssues.push(`${label}: GST% missing on '${item.name}' — ${item.custom ? 'enter it above' : 'set it in the Pricelist'}.`); warn(field, `'${item.name}' has no GST%${item.custom ? '' : ' in the Pricelist'}.`); }
    if (m === null) { pricingIssues.push(`${label}: margin% missing on '${item.name}' — set it in the Pricelist.`); warn(field, `'${item.name}' has no margin% in the Pricelist.`); }
    return [g, m];
  };
  const [panelGst, panelMargin] = itemPricing(panel, 'panel_item_id', 'Panels');
  const panelSell = sellPrice(panel);
  const panelReal = panel && panelSell > 0 && panelCount > 0;
  const panelCost = panelOff ? 0 : (panelReal ? panelSell * panelCount : PANEL_BENCH_SHARE * baseKwp * kw);
  const [inverterGst, inverterMargin] = itemPricing(inverter, 'inverter_item_id', 'Inverter');
  const inverterSell = sellPrice(inverter);
  const inverterReal = inverter && inverterSell > 0;
  const inverterCost = inverterOff ? 0 : (inverterReal ? inverterSell : INVERTER_BENCH_SHARE * typeKwp * kw);
  const [batteryGst, batteryMargin] = needsBattery ? itemPricing(battery, 'battery_item_id', 'Battery') : [null, null];
  const batterySell = sellPrice(battery);
  let batteryCost = 0, batteryBenchmark = false;
  if (needsBattery && batteryCount > 0) {
    if (battery && batterySell > 0) batteryCost = batterySell * batteryCount;
    else { batteryCost = batteryCount * batteryUnitKwh * num(config?.battery_benchmark_per_kwh, 20000); batteryBenchmark = true; }
  }
  if (!panelReal && !panelOff && kw > 0) pricingIssues.push('Panels: benchmark price used — pick a panel from Inventory (or type one in) to price with its GST% & margin%.');
  if (!inverterReal && !inverterOff && kw > 0) pricingIssues.push('Inverter: benchmark price used — pick an inverter from Inventory (or type one in) to price with its GST% & margin%.');

  const bosAuto = BOS_SHARE * baseKwp * kw;
  const legacyBos = isSet(overrides.bos_cost) ? num(overrides.bos_cost) : null;
  const serviceLines = {};
  Object.entries(BOS_SPLIT).forEach(([key, share]) => {
    const autoCost = (legacyBos !== null ? legacyBos : bosAuto) * share;
    const cost = isSet(overrides[`${key}_cost`]) ? num(overrides[`${key}_cost`]) : autoCost;
    const g = pct(overrides[`${key}_gst_pct`]), m = pct(overrides[`${key}_margin_pct`]);
    const label = key.charAt(0).toUpperCase() + key.slice(1);
    if (kw > 0 && g === null) { pricingIssues.push(`${label}: GST% not set.`); warn(`${key}_gst_pct`, `${label} line needs its own GST%.`); }
    if (kw > 0 && m === null) { pricingIssues.push(`${label}: margin% not set.`); warn(`${key}_margin_pct`, `${label} line needs its own margin%.`); }
    const amount = cost * (1 + (m || 0) / 100);
    serviceLines[key] = { cost: Math.round(cost), auto: Math.round(autoCost), amount: Math.round(amount), gst_pct: g, margin_pct: m, gst_amount: Math.round(amount * (g || 0) / 100), gst_missing: g === null, margin_missing: m === null };
  });
  const bosCost = Object.values(serviceLines).reduce((s, l) => s + l.amount, 0);

  let totalCost = kw > 0 ? Math.round(panelCost + inverterCost + batteryCost + bosCost) : 0;
  let totalGst = kw > 0 ? (panelCost * (panelGst || 0) / 100 + inverterCost * (inverterGst || 0) / 100 + batteryCost * (batteryGst || 0) / 100 + Object.values(serviceLines).reduce((s, l) => s + l.gst_amount, 0)) : 0;
  const itemisedTotal = totalCost, itemisedGst = Math.round(totalGst);
  const slab = kw > 0 ? slabPriceFor(slabDoc, kw) : null;
  let pricingSource = 'itemised';
  let pricingIssuesOut = pricingIssues;
  if (slab && inputs.pricing_mode === 'slab') {
    pricingSource = 'slab'; totalCost = slab.total; totalGst = slab.gst_amount || 0;
    pricingIssuesOut = slab.gst_missing ? [`Slab rate for ${slab.category} has no GST% — set it in Price list → Package slabs.`] : [];
  }
  const subsidy = Math.max(num(inputs.subsidy), 0);
  if (totalCost > 0 && subsidy > totalCost) warn('subsidy', `Subsidy ₹${fmt(subsidy)} is more than the system cost ₹${fmt(totalCost)} — check the amount.`);
  const netCost = Math.max(totalCost - subsidy, 0);

  const annualGen = kw * sy * 365;
  const monthlyGen = annualGen / 12;
  const offsetUnits = units > 0 ? Math.min(monthlyGen, units) : monthlyGen;
  const exportUnits = units > 0 && gridTied ? Math.max(monthlyGen - units, 0) : 0;
  const energySaving = offsetUnits * tariff;
  const exportIncome = exportUnits * exportRate;
  const networkMonthly = kw > 0 ? (networkBasis === 'per_kw_month' ? kw * networkCharge : monthlyGen * networkCharge) : 0;
  const monthlySaving = energySaving + exportIncome - networkMonthly;
  const annualSaving = monthlySaving * 12;
  const paybackYears = annualSaving > 0 && netCost > 0 ? round(netCost / annualSaving, 1) : (netCost === 0 && annualSaving > 0 ? 0 : null);
  if (units > 0 && monthlyGen > units * 1.25) {
    if (exportRate > 0) warn('system_size_kw', `System makes ~${fmt(monthlyGen)} units/month but the customer uses ${fmt(units)} — the extra ~${fmt(exportUnits)} units are sold to the grid at ₹${exportRate}/unit.`);
    else warn('system_size_kw', `System makes ~${fmt(monthlyGen)} units/month but the customer uses ${fmt(units)} — savings are capped at what they use.`);
  }
  if (kw > 0 && monthlySaving <= 0 && networkMonthly > 0) warn('network_charge', `The network charge (₹${fmt(networkMonthly)}/month) wipes out the saving — check the rate.`);

  const life = Math.trunc(num(config?.system_life_years, 25)) || 25;
  const deg = num(config?.panel_degradation_pct_per_year, 0.7) / 100;
  const monthlyBillNow = units > 0 ? units * tariff : bill;
  const yearly = [];
  let cumWithout = 0, cumWith = netCost, lifetime = 0;
  for (let y = 1; y <= life; y++) {
    const savingY = annualSaving * (1 - deg) ** (y - 1);
    lifetime += savingY;
    cumWithout += monthlyBillNow * 12;
    cumWith += Math.max(monthlyBillNow * 12 - savingY, 0);
    yearly.push({ year: y, without_solar: Math.round(cumWithout), with_solar: Math.round(cumWith) });
  }
  const subsidyRef = customerType === 'residential' && (systemType === 'on-grid' || systemType === 'hybrid') && kw > 0 ? pmSuryaGharReference(kw, config) : 0;

  return {
    system_type: systemType, customer_type: customerType, tariff_per_unit: tariff, specific_yield: sy,
    monthly_eb_units: Math.round(units), units_source: unitsSource, daily_units: round(daily, 2),
    system_size_kw_auto: kwAuto, system_size_kw: kw, roof_cap_kw: roofCapKw,
    panel_wattage_w: panelW || null, panel_count_auto: panelCountAuto, panel_count: panelCount,
    inverter_rated_kw: inverterKw || null,
    backup_hours: needsBattery ? backupHours : null, battery_kwh_needed: needsBattery ? batteryKwhNeeded : null,
    battery_unit_kwh: needsBattery ? batteryUnitKwh : null, battery_count_auto: batteryCountAuto, battery_count: batteryCount,
    lines: {
      panels: { amount: Math.round(panelCost), benchmark: !panelReal && !panelOff, excluded: panelOff, custom: !!panel?.custom, unit_price: panel ? round(panelSell, 2) : null, gst_pct: panelGst, margin_pct: panelMargin, gst_amount: Math.round(panelCost * (panelGst || 0) / 100) },
      inverter: { amount: Math.round(inverterCost), benchmark: !inverterReal && !inverterOff, excluded: inverterOff, custom: !!inverter?.custom, unit_price: inverter ? round(inverterSell, 2) : null, gst_pct: inverterGst, margin_pct: inverterMargin, gst_amount: Math.round(inverterCost * (inverterGst || 0) / 100) },
      battery: needsBattery ? { amount: Math.round(batteryCost), benchmark: batteryBenchmark, unit_price: battery ? round(batterySell, 2) : null, gst_pct: batteryGst, margin_pct: batteryMargin, gst_amount: Math.round(batteryCost * (batteryGst || 0) / 100) } : null,
      structure: serviceLines.structure, cabling: serviceLines.cabling, installation: serviceLines.installation,
      bos: { amount: Math.round(bosCost), auto: Math.round(bosAuto) },
    },
    total_cost: totalCost, total_gst: Math.round(totalGst), total_incl_gst: Math.round(totalCost + totalGst), pricing_issues: pricingIssuesOut, pricing_source: pricingSource, slab, itemised_total: itemisedTotal, itemised_gst: itemisedGst, subsidy: Math.round(subsidy), subsidy_reference: subsidyRef, net_cost: Math.round(netCost),
    annual_generation_units: Math.round(annualGen), monthly_generation_units: Math.round(monthlyGen),
    monthly_bill_now: Math.round(monthlyBillNow), monthly_saving: Math.round(monthlySaving), annual_saving: Math.round(annualSaving),
    billing_cycle: billingCycle, period_months: period, bill_units: Math.round(units * period),
    bill_now_per_period: Math.round(monthlyBillNow * period), saving_per_period: Math.round(monthlySaving * period),
    energy_saving_monthly: Math.round(energySaving), export_units_monthly: Math.round(exportUnits), export_rate: exportRate,
    export_income_monthly: Math.round(exportIncome), network_charge: networkCharge, network_charge_basis: networkBasis,
    network_charge_monthly: Math.round(networkMonthly),
    payback_years: paybackYears, lifetime_savings: Math.round(lifetime),
    roi_pct: netCost > 0 ? round(annualSaving / netCost * 100, 1) : null,
    yearly, warnings,
  };
}

/** Project the engine result onto the `proposed_solution` shape the rest of the app reads. */
export function projectResult(data, r) {
  return {
    ...data,
    system_size_kw: r.system_size_kw, panel_count: r.panel_count, battery_count: r.battery_count,
    monthly_eb_units: r.monthly_eb_units, tariff_per_unit: r.tariff_per_unit,
    total_cost: r.total_cost, net_cost: r.net_cost,
    _derived: {
      annual_savings: r.annual_saving, monthly_savings: r.monthly_saving, payback_years: r.payback_years, roi_pct: r.roi_pct,
      annual_generation_units: r.annual_generation_units, lifetime_savings: r.lifetime_savings, monthly_units: r.monthly_eb_units,
    },
    _quick: r,
  };
}
